import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Quote } from 'lucide-react';

// Full-bleed parallax image with quote overlay
// Image scrolls at a different rate than the content for depth effect
const ParallaxQuote = () => {
  const ref = useRef(null);

  // Parallax tied to scroll position of this section
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start']
  });

  // Background moves slower than foreground (classic parallax)
  const y = useTransform(scrollYProgress, [0, 1], ['-15%', '15%']);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [1.1, 1.15, 1.1]);
  const textY = useTransform(scrollYProgress, [0, 1], ['20%', '-20%']);

  return (
    <section
      ref={ref}
      className="relative overflow-hidden h-[70vh] sm:h-[80vh] lg:h-[90vh]"
    >
      {/* Parallax background image */}
      <motion.div
        className="absolute inset-0 w-full h-[130%]"
        style={{ y, scale }}
      >
        <img
          src="/clubshero.png"
          alt="Free State golf"
          className="w-full h-full object-cover"
        />
        {/* Dark gradient overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-transparent to-black/40" />
      </motion.div>

      {/* Quote content - moves opposite direction for depth */}
      <motion.div
        className="relative z-10 h-full flex items-center justify-center px-6 sm:px-8"
        style={{ y: textY }}
      >
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <Quote className="w-10 h-10 sm:w-12 sm:h-12 text-fsgu-green-400 mx-auto mb-6 sm:mb-8 opacity-80" />

            <blockquote className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold font-cursive text-white leading-[1.15] mb-6 sm:mb-8 drop-shadow-2xl">
              Golf is a game of
              <br />
              <span className="text-fsgu-green-400">integrity and tradition</span>
              <br />
              played on the
              <br />
              honor system.
            </blockquote>

            <div className="flex items-center justify-center gap-4">
              <div className="h-px w-12 sm:w-16 bg-fsgu-green-400" />
              <p className="text-sm sm:text-base uppercase tracking-[0.25em] text-white/90 font-medium">
                Free State Golf Union
              </p>
              <div className="h-px w-12 sm:w-16 bg-fsgu-green-400" />
            </div>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};

export default ParallaxQuote;